import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  canLoadWorkflowsFromSupabase,
  loadWorkflowBundle,
  resolveCurrentNodeText,
  templateForToken,
} from "./workflowAdapter";
import { FALLBACK_WORKFLOWS, type WorkflowTemplate } from "./workflowFallback";

type BoardWorkflowApi = {
  source: WorkflowTemplate["source"];
  settled: boolean;
  templateFor: (token: string) => WorkflowTemplate | null;
  currentNodeTextFor: (runId?: number) => string | null;
};

const BoardWorkflowContext = createContext<BoardWorkflowApi | null>(null);

function useBoardWorkflowState(): BoardWorkflowApi {
  const [templates, setTemplates] = useState(FALLBACK_WORKFLOWS);
  const [runs, setRuns] = useState<
    Parameters<typeof resolveCurrentNodeText>[1]
  >({});
  const [source, setSource] = useState<WorkflowTemplate["source"]>("fallback");
  const [settled, setSettled] = useState(!canLoadWorkflowsFromSupabase());

  useEffect(() => {
    let cancelled = false;
    void loadWorkflowBundle().then((loaded) => {
      if (cancelled) return;
      if (loaded) {
        setTemplates(loaded.templates);
        setRuns(loaded.runs);
        setSource("db");
      }
      setSettled(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(
    () => ({
      source,
      settled,
      templateFor: (token: string) =>
        templateForToken(token, templates, source),
      currentNodeTextFor: (runId?: number) =>
        resolveCurrentNodeText(runId, runs, templates),
    }),
    [runs, settled, source, templates],
  );
}

export function BoardWorkflowProvider({ children }: { children: ReactNode }) {
  const value = useBoardWorkflowState();
  return createElement(BoardWorkflowContext.Provider, { value }, children);
}

export function useBoardWorkflow() {
  const ctx = useContext(BoardWorkflowContext);
  if (!ctx) {
    throw new Error(
      "useBoardWorkflow must be used inside BoardWorkflowProvider",
    );
  }
  return ctx;
}
